import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useContractService } from "contexts/ServiceContext";
import useSelectedContractDocEntry from "hooks/useSelectedContractDocEntry";
import { ApiStatusException } from "services/apiErrors";
import ContractService from "services/ContractService";
import {
  AI_EVENTS,
  EventTrackFunction,
  useTrackEvent,
} from "../../useAppInsight";

const handleTrackError = (
  trackQueryError: EventTrackFunction,
  eventId: string,
  err: ApiStatusException
) => {
  trackQueryError(
    {
      statusCode: err.statusCode as number,
      message: err.message,
    },
    eventId
  );
  throw err;
};

const CONTRACT_QUERY_KEY = "contract";

export const contractQueryKeyFactory = (docEntry?: number) => [
  CONTRACT_QUERY_KEY,
  { docEntry },
];

const useContractDetails = (_docEntry?: number): UseQueryResult<Contract> => {
  const contractService = useContractService();
  const selectedContractDocEntry = useSelectedContractDocEntry();
  const docEntry = _docEntry ?? selectedContractDocEntry;
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    docEntry,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    docEntry,
  });
  return useQuery({
    queryKey: contractQueryKeyFactory(docEntry),
    queryFn: () => {
      const eventId = trackQuery({
        endpoint: "Quotation({DocEntry})",
      });
      return docEntry
        ? contractService
            .getContractDetails(docEntry)
            .catch((err) => handleTrackError(trackQueryError, eventId, err))
        : Promise.reject();
    },
    enabled: !!docEntry,
    select: (data) => data.contract,
  });
};

type ListTermsReturnType = Awaited<
  ReturnType<ContractService["listContractTerms"]>
>;

export const useContractTerms = (
  selectedContractDocEntry?: number,
  filters?: Partial<OrdersFilters>,
  currentSort?: { key: string; direction: string }
): UseQueryResult<ListTermsReturnType> => {
  const contractService = useContractService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    docEntry: selectedContractDocEntry,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    docEntry: selectedContractDocEntry,
  });

  return useQuery({
    queryKey: [
      CONTRACT_QUERY_KEY,
      { docEntry: selectedContractDocEntry },
      "terms",
      { filters, currentSort },
    ],
    queryFn: () => {
      const eventId = trackQuery({
        endpoint: "QuotationLine",
        parameters: { filters, sorting: currentSort },
      });
      return selectedContractDocEntry
        ? contractService
            .listContractTerms(
              selectedContractDocEntry,

              {
                shipFrom: filters?.shipFrom ?? [],
                shipTo: filters?.shipTo ?? [],
                mode: filters?.mode ?? [],
              },
              currentSort
            )
            .catch((err) => handleTrackError(trackQueryError, eventId, err))
        : Promise.reject();
    },
    enabled: !!selectedContractDocEntry,
  });
};

export default useContractDetails;
